import React, { useState } from "react";

import CircularProgress from "@material-ui/core/CircularProgress";
import CloseIcon from "@material-ui/icons/Close";
import EditIcon from "@material-ui/icons/Edit";
import CheckIcon from "@material-ui/icons/Check";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";

/**
 * Não existe no projeto um padrão pronto de "edição inline por campo" (lápis
 * -> input no lugar -> salvar/cancelar): o único "editar" existente
 * (Contatos, ContactForm/ContactModal) abre um formulário inteiro num
 * modal. Este componente é novo, seguindo o vocabulário visual já usado no
 * projeto (TextField MUI v4 com error/helperText, ícones de
 * @material-ui/icons) — não uma adaptação de algo existente.
 */
const useStyles = makeStyles((theme) => ({
  row: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(0.5),
    minHeight: 32,
  },
  display: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  editBox: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(0.5),
    flex: 1,
    flexWrap: "wrap",
  },
  input: {
    minWidth: 180,
  },
  error: {
    fontSize: 12,
    color: theme.palette.error.main,
    marginTop: 2,
  },
}));

const EditableField = ({ label, value, rawValue, type = "text", onSave, disabled = false, disabledHint }) => {
  const classes = useStyles();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const startEditing = () => {
    setDraft(rawValue != null ? rawValue : value || "");
    setError(null);
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setError(null);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(draft);
      setEditing(false);
    } catch (err) {
      setError(err?.response?.data?.error || "Não foi possível salvar.");
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className={classes.row}>
        <div className={classes.display}>
          <Typography variant="body2">
            <strong>{label}:</strong> {value || "—"}
          </Typography>
          <Tooltip title={disabled ? disabledHint || `${label} indisponível` : `Editar ${label.toLowerCase()}`}>
            <span>
              <IconButton size="small" onClick={startEditing} disabled={disabled}>
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.row}>
      <div className={classes.editBox}>
        <Typography variant="body2" style={{ paddingTop: 6 }}>
          <strong>{label}:</strong>
        </Typography>
        <div>
          <TextField
            className={classes.input}
            size="small"
            variant="outlined"
            type={type}
            value={draft}
            disabled={saving}
            autoFocus
            onChange={(event) => setDraft(event.target.value)}
            InputLabelProps={type === "date" ? { shrink: true } : undefined}
          />
          {error ? <div className={classes.error}>{error}</div> : null}
        </div>
        <Tooltip title="Salvar">
          <span>
            <IconButton size="small" onClick={save} disabled={saving}>
              {saving ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" color="primary" />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Cancelar">
          <span>
            <IconButton size="small" onClick={cancel} disabled={saving}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </div>
    </div>
  );
};

export default EditableField;
